<template>
  <div class="ais-table-wrapper">
    <b-alert v-if="error" variant="danger" show class="mb-2">
      {{ error }}
    </b-alert>
    <div class="ais-table-scroll">
      <b-table
        ref="table"
        small
        hover
        head-variant="light"
        :items="targets"
        :fields="fields"
        :busy="loading"
        :empty-text="emptyText"
        :tbody-tr-class="rowClass"
        :tbody-tr-attr="rowAttrs"
        :sort-icon-left="true"
        :selectable="true"
        :select-mode="'single'"
        :selected-rows="selectedRows"
        :selected-variant="'danger'"
        class="styled-table ais-table"
        @row-selected="onRowSelected"
      >
        <template #table-busy>
          <div class="text-center text-muted my-2">
            <b-spinner small class="mr-2" />
            Loading AIS targets...
          </div>
        </template>
        <template #cell(name)="{ item }">
          <div class="ais-name-cell">
            <strong>{{ item.name }}</strong>
            <span v-if="item.imo" class="ais-name-meta">IMO: {{ item.imo }}</span>
            <span v-if="item.mmsi" class="ais-name-meta">MMSI: {{ item.mmsi }}</span>
            <span v-if="formatShipType(item.type) !== 'Unknown'" class="ais-name-meta">{{ formatShipType(item.type) }}</span>
          </div>
        </template>
        <template #cell(speed)="{ item }">
          {{ formatSpeed(item.speed) }}
        </template>
        <template #cell(course)="{ item }">
          {{ formatCourse(item.course) }}
        </template>
        <template #cell(navStatus)="{ item }">
          {{ formatNavStatus(item.navStatus) }}
        </template>
        <template #cell(received)="{ item }">
          {{ formatTimestamp(item.received) }}
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'

const NAV_STATUS_MAP = {
  0: 'Under way using engine',
  1: 'At anchor',
  2: 'Not under command',
  3: 'Restricted manoeuvrability',
  4: 'Constrained by draught',
  5: 'Moored',
  6: 'Aground',
  7: 'Fishing',
  8: 'Under way sailing',
  9: 'Reserved',
  10: 'Reserved',
  11: 'Reserved',
  12: 'Reserved',
  13: 'Reserved',
  14: 'AIS-SART active',
  15: 'Status not available',
  21: 'AIS test target',
}

const SHIP_TYPE_MAP = {
  0: 'Unknown',
  3: 'Tug, Pilot',
  4: 'High Speed',
  6: 'Passenger',
  7: 'Cargo',
  8: 'Tanker',
  9: 'Yachts',
  10: 'Fishing',
  11: 'Base Stations',
  12: 'Air Craft',
  13: 'Navigation Aid',
}

export default {
  name: 'AISTable',
  props: {
    targets: {
      type: Array,
      default: () => [],
    },
    selectedId: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
  },
  computed: {
    fields() {
      return [
        { key: 'name', label: 'Name', sortable: true, thClass: 'text-left', tdClass: 'text-left' },
        { key: 'speed', label: 'Speed', sortable: true, thClass: 'text-left', tdClass: 'text-left', thStyle: 'white-space: nowrap;' },
        { key: 'course', label: 'Course', sortable: false, thClass: 'text-left', tdClass: 'text-left' },
        { key: 'navStatus', label: 'Status', sortable: false, thClass: 'text-left', tdClass: 'text-left' },
        { key: 'received', label: 'Time (UTC)', sortable: false, thClass: 'text-left', tdClass: 'text-left', thStyle: 'white-space: nowrap;' },
      ]
    },
    emptyText() {
      return this.loading ? 'Loading...' : 'No AIS targets in the current view'
    },
    selectedRows() {
      return this.targets.filter(item => item.id === this.selectedId)
    },
  },
  watch: {
    selectedId: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          if (this.selectedId) {
            this.scrollToTarget(this.selectedId)
          }
        })
      },
    },
  },
  methods: {
    onRowSelected(items) {
      if (items.length > 0) {
        this.$emit('select', items[0])
      } else {
        this.$emit('select', null)
      }
    },
    rowClass(item) {
      if (!item) {
        return null
      }
      return item.id === this.selectedId ? 'ais-row-selected' : null
    },
    rowAttrs(item) {
      if (!item) {
        return {}
      }
      return {
        'data-target-id': item.id,
      }
    },
    formatSpeed(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) {
        return '—'
      }
      return `${num.toFixed(1)} kn`
    },
    formatCourse(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) {
        return '—'
      }
      const normalized = this.normalizeCourse(num)
      return `${normalized.toFixed(1)}°`
    },
    formatNavStatus(code) {
      if (code === null || code === undefined) {
        return 'Unknown'
      }
      const mapped = NAV_STATUS_MAP[code]
      const rawText = mapped || (typeof code === 'string' ? code : `Status ${code}`)
      if (typeof rawText === 'string' && rawText.trim().toLowerCase() === 'status not available') {
        return '—'
      }
      return rawText
    },
    formatShipType(code) {
      if (code === null || code === undefined || code === '') {
        return '—'
      }
      const rawString = String(code).trim()
      const numeric = Number(rawString)
      if (Number.isFinite(numeric)) {
        const direct = SHIP_TYPE_MAP[numeric]
        if (direct) {
          return direct
        }
        const tens = Math.floor(numeric / 10) * 10
        return SHIP_TYPE_MAP[tens] || 'Unknown type'
      }
      if (!rawString) {
        return '—'
      }
      return SHIP_TYPE_MAP[rawString] || rawString
    },
    formatTimestamp(value) {
      if (!value) {
        return '—'
      }
      const ts = dayjs(value)
      if (!ts.isValid()) {
        return value
      }
      return ts.utc().format('HH:mm')
    },
    scrollToTarget(id) {
      if (!id) {
        return
      }
      this.$nextTick(() => {
        const table = this.$refs.table?.$el
        if (!table) {
          return
        }
        const selector = `tr[data-target-id="${this.escapeSelector(id)}"]`
        const row = table.querySelector(selector)
        if (row && typeof row.scrollIntoView === 'function') {
          row.scrollIntoView({ block: 'nearest' })
        }
      })
    },
    escapeSelector(value) {
      if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(String(value))
      }
      return String(value)
    },
    normalizeCourse(value) {
      if (!Number.isFinite(value)) {
        return value
      }
      const normalized = value % 360
      if (normalized < 0) {
        return normalized + 360
      }
      return normalized
    },
  },
}
</script>

<style scoped>
.ais-table-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px 20px 18px;
  overflow: hidden;
  background: linear-gradient(135deg, #fbfcff 0%, #f6f8ff 100%);
  border: 1px solid #e4e8f5;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(34, 45, 80, 0.07);
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
  min-height: 0;
}

.ais-table-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.ais-table-scroll :deep(.table) {
  margin-bottom: 0;
  background: transparent;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
}

.ais-table-scroll :deep(.table thead tr) {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.72rem;
  color: #5c6685;
  background: #eef1fb;
}

.ais-table-scroll :deep(.table tbody tr) {
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.ais-table-scroll :deep(.table tbody tr:hover) {
  transform: translateY(-1px);
  box-shadow: 0 10px 18px rgba(15, 23, 42, 0.09);
  background: #ffffff;
}

.ais-table-scroll :deep(.table td) {
  vertical-align: middle;
  padding: 0.75rem;
  border-top: none;
  font-size: 0.78rem;
  font-weight: 500;
  color: #1f2940;
}

.ais-row-selected {
  background-color: #e0f2fe !important;
}

.ais-row-selected td {
  background-color: #e0f2fe !important;
}

.ais-name-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ais-name-meta {
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #4a5374;
}

.ais-name-meta--muted {
  color: #94a3b8;
}

.speed-cell {
  white-space: nowrap;
}

.ais-table-wrapper :deep(.spinner-border) {
  color: #4b5fdd;
}

.ais-table-wrapper :deep(.alert) {
  border-radius: 10px;
  font-size: 0.78rem;
}
</style>
